import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import IconMenu from '../Icon/IconMenu';
import Swal from 'sweetalert2';
 
function SearchPanel({ columns = [], columnTypeMapping = {}, onFilterChange = () => {}, onApply = () => {}, onClear = () => {} }) {
  const dispatch = useDispatch();
  const [filters, setFilters] = useState({});
 
  useEffect(() => {
    // Initialize filters if not already set
    if (Object.keys(filters).length === 0) {
      const initialFilters = columns.reduce((acc, column) => {
        acc[column] = { checked: false, type: '', value: '' };
        return acc;
      }, {});
      setFilters(initialFilters);
    }
  }, [columns]); // Only initialize once when columns change
 
  const handleCheckboxChange = (column) => {
    const dataType = columnTypeMapping[column] || 'string';
    const initialType = dataType === 'number' ? '=' : 'is'; // Set initial type based on column type
 
    setFilters((prev) => {
      const updatedFilters = {
        ...prev,
        [column]: {
          ...prev[column],
          checked: !prev[column].checked,
          type: !prev[column].checked ? initialType : '', // Use initialType based on column data type
        },
      };
      onFilterChange(updatedFilters); // Notify parent of the change
      return updatedFilters;
    });
  };
 
  const handleTypeChange = (column, type) => {
    setFilters((prev) => {
      const updatedFilters = {
        ...prev,
        [column]: { ...prev[column], type, value: '' }, // Corrected line
      };
      onFilterChange(updatedFilters); // Notify parent of the change
      return updatedFilters;
    });
  };
 
  const handleValueChange = (column, value) => {
    setFilters((prev) => {
      const updatedFilters = {
        ...prev,
        [column]: { ...prev[column], value },
      };
      onFilterChange(updatedFilters); // Notify parent of the change
      return updatedFilters;
    });
  };
 
  const handleRangeValueChange = (column, value, index) => {
    setFilters((prev) => {
      const currentValues = prev[column].value.split(',') || ['', ''];
      currentValues[index] = value;
      const updatedFilters = {
        ...prev,
        [column]: { ...prev[column], value: currentValues.join(',') },
      };
      onFilterChange(updatedFilters); // Notify parent of the change
      return updatedFilters;
    });
  };
 
  const handleClear = () => {
    setFilters((prev) => {
      const clearedFilters = Object.fromEntries(
        Object.keys(prev).map((key) => [key, { checked: false, type: '', value: '' }])
      );
      return clearedFilters;
    });
 
    Swal.fire({
      icon: 'info',
      title: 'Filters Cleared',
      toast: true,
      position: 'top',
      showConfirmButton: false,
      timer: 2000,
      padding: '10px 20px',
    });
  };
 
  const handleClearFilter = () => {
    handleClear();
    onClear();
 
    Swal.fire({
      icon: 'info',
      title: 'Filters Cleared and Data Refreshed',
      toast: true,
      position: 'top',
      showConfirmButton: false,
      timer: 2000,
      padding: '10px 20px',
    });
  };
 
  const handleSaveFilter = () => {
    Swal.fire({
      icon: 'success',
      title: 'Saved Filters',
      toast: true,
      position: 'top',
      showConfirmButton: false,
      timer: 3000,
      padding: '10px 20px',
    });
  };
 
  const handleApply = () => {
    onApply();
    Swal.fire({
      icon: 'success',
      title: 'Filters Applied',
      toast: true,
      position: 'top',
      showConfirmButton: false,
      timer: 3000,
      padding: '10px 20px',
    });
  };
 
  const stringOptions = [
    { value: 'is', label: 'is' },
    { value: "isn't", label: "isn't" },
    { value: 'contains', label: 'contains' },
    { value: "doesn't contain", label: "doesn't contain" },
    { value: 'starts with', label: 'starts with' },
    { value: 'ends with', label: 'ends with' },
    { value: 'is empty', label: 'is empty' },
    { value: 'is not empty', label: 'is not empty' },
  ];
 
  const numberOptions = [
    { value: '=', label: '=' },
    { value: '!=', label: '!=' },
    { value: '<', label: '<' },
    { value: '<=', label: '<=' },
    { value: '>', label: '>' },
    { value: '>=', label: '>=' },
    { value: 'between', label: 'between' },
    { value: 'not between', label: 'not between' },
    { value: 'is empty', label: 'is empty' },
    { value: 'is not empty', label: 'is not empty' },
  ];
 
  const [isPanelOpen, setIsPanelOpen] = useState(true);
 
  const togglePanel = () => {
    setIsPanelOpen(!isPanelOpen);
  };
 
  return (
    <div className="relative flex pb-5">
      <div className="absolute top-0 pb-10 left-3 z-20">
        <button type="button" className="p-2" onClick={togglePanel}>
          <IconMenu className="text-gray-700 dark:text-gray-300" />
        </button>
      </div>
      <div
        className={`panel p-4 flex-col w-[290px] max-w-full xl:relative z-10 space-y-4 xl:h-auto xl:block ltr:xl:rounded-r-md ltr:rounded-r-none rtl:xl:rounded-l-md rtl:rounded-l-none ${
          isPanelOpen ? 'block' : 'hidden'
        } transform ${isPanelOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out`}
        style={{
          position: isPanelOpen ? 'relative' : 'absolute',
          left: isPanelOpen ? '0' : '-140px',
          overflowY: 'auto',
          maxHeight: '100vh',
        }}
      >
        {isPanelOpen && (
          <>
            <div className="flex flex-col relative sm:flex-auto pt-4">
              <div className="flex flex-wrap items-center justify-center gap-2 mb-3">
                <button type="button" onClick={handleSaveFilter} className="btn btn-sm btn-secondary">
                  Save Filter
                </button>
                <button onClick={handleClearFilter} className="btn btn-sm btn-dark">
                  Clear Filter
                </button>
              </div>
              {columns.map((column) => {
                const dataType = columnTypeMapping[column] || 'string';
                const options = dataType === 'number' ? numberOptions : stringOptions;
 
                return (
                  <div key={column}>
                    <label className="inline-flex flex-auto h-auto gap-2">
                      <input
                        type="checkbox"
                        checked={filters[column]?.checked || false}
                        onChange={() => handleCheckboxChange(column)}
                        className="form-checkbox outline-secondary"
                      />
                      {column}
                    </label>
                    {filters[column]?.checked && (
                      <div className="flex flex-col gap-2 mb-2">
                        <select
                          className="form-select form-select-sm text-white-dark border-2"
                          value={filters[column].type}
                          onChange={(e) => handleTypeChange(column, e.target.value)}
                        >
                          {options.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
 
                        {filters[column].type !== 'is empty' && filters[column].type !== 'is not empty' && (
                          <>
                            {['between', 'not between'].includes(filters[column].type) ? (
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={filters[column].value.split(',')[0] || ''}
                                  onChange={(e) => handleRangeValueChange(column, e.target.value, 0)}
                                  placeholder="Enter start value..."
                                  className="form-input form-input-sm"
                                />
                                <input
                                  type="text"
                                  value={filters[column].value.split(',')[1] || ''}
                                  onChange={(e) => handleRangeValueChange(column, e.target.value, 1)}
                                  placeholder="Enter end value..."
                                  className="form-input form-input-sm"
                                />
                              </div>
                            ) : (
                              <input
                                type="text"
                                value={filters[column].value}
                                onChange={(e) => handleValueChange(column, e.target.value)}
                                placeholder="Enter value..."
                                className="form-input form-input-sm"
                              />
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
 
            <div className="flex flex-wrap items-center justify-center gap-2 mb-3">
              <button onClick={handleApply} className="btn btn-sm btn-secondary">
                Apply
              </button>
              <button onClick={handleClear} className="btn btn-sm btn-dark">
                Clear
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
 
export default SearchPanel;
 